import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { privatize } from '@/lib/utils/revenueUtils'
import { EmptyValue, GoogleAdsMark, MetaMark, MoneyTile, MoneyTiles, StepArrow, StripeMark } from './AttributionParts'
import { fmtCount, fmtMoney } from './attributionView'
import { ReportHeader } from './AttributionReports'

/**
 * Attribution states with little or no data (Paper "03 - App · Attribution"
 * and "Attribution · Loading"): nothing connected, no payments in range, the
 * first Stripe sync, and the loading skeleton. Presentational: the page picks
 * the state (`selectAttributionView`) and passes plain props.
 */

// ---------------------------------------------------------------------------
// Loading (Paper "Attribution · Loading · 1440")
// ---------------------------------------------------------------------------

const SKELETON_ROWS = [
  [220, 228, 47, 47],
  [235, 155, 31, 47],
  [178, 262, 31, 47],
  [227, 444, 47, 31],
  [110, 276, 16, 31],
]

function TileSkeleton({ label, first, wide }: { label?: string; first?: boolean; wide?: boolean }) {
  return (
    <div
      className={cn(
        'flex min-w-[140px] flex-1 basis-0 flex-col gap-1.5 px-[18px] py-4',
        !first && 'border-l border-ui-hairline',
        first && label && 'shadow-[inset_0_-1.5px_0_color-mix(in_oklab,var(--ui-title)_80%,transparent)]',
      )}
    >
      {label ? <span className="text-xs leading-4 text-muted-foreground">{label}</span> : <Skeleton className="my-[3px] h-2.5 w-[88px]" />}
      <span className="flex h-7 items-center gap-1.5">
        <Skeleton className="h-6 w-[81px]" />
        {wide && <Skeleton className="h-3 w-[27px]" />}
      </span>
    </div>
  )
}

/** Chart skeleton: axis ticks, seven day pairs of equal height (no fake shape). */
function ChartSkeleton({ legend }: { legend?: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 px-5 pt-4 pb-[18px]">
      <div className="flex h-4 items-center gap-4">
        {legend ?? (
          <>
            <Skeleton className="h-2.5 w-24" />
            <Skeleton className="h-2.5 w-[88px]" />
            <span className="flex-1" />
            <Skeleton className="hidden h-2.5 w-[300px] sm:block" />
          </>
        )}
      </div>
      <div className="flex h-[228px] gap-3 pt-3">
        <div className="flex w-[34px] shrink-0 flex-col justify-between pb-7">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-2.5 w-[34px] rounded-[3px]" />
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-1 items-end border-b border-border">
            {Array.from({ length: 7 }).map((_, i) => (
              <span key={i} className="flex min-w-0 flex-1 justify-center gap-1">
                <Skeleton className="h-[72px] w-[26px]" />
                <Skeleton className="h-[72px] w-[26px]" />
              </span>
            ))}
          </div>
          <div className="flex h-7 items-center">
            {Array.from({ length: 7 }).map((_, i) => (
              <span key={i} className="flex min-w-0 flex-1 justify-center">
                <Skeleton className="h-2 w-9" />
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function TableSkeleton({ title, columns }: { title?: string; columns?: string[] }) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center gap-4 border-b border-ui-hairline px-[18px] py-3.5">
        {title ? (
          <h3 className="text-sm font-medium leading-[18px] text-foreground">{title}</h3>
        ) : (
          <Skeleton className="h-3.5 w-24" />
        )}
        <span className="flex-1" />
        <Skeleton className="hidden h-7 w-[330px] rounded-lg sm:block" />
      </div>
      <div className="flex items-center border-b border-ui-hairline bg-muted/60 px-[18px] py-2.5 text-xs leading-4 text-muted-foreground">
        {columns ? (
          <>
            <span className="flex-1">{columns[0]}</span>
            {columns.slice(1).map((c, i) => (
              <span key={c} className={cn('hidden justify-end sm:flex', i === columns.length - 2 ? 'w-[200px]' : 'w-[150px]')}>{c}</span>
            ))}
          </>
        ) : (
          <Skeleton className="h-2 w-16" />
        )}
      </div>
      {SKELETON_ROWS.map(([name, sub, a, b], i) => (
        <div key={i} className="flex items-center border-b border-ui-rowline px-[18px] py-3.5 last:border-b-0">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            <Skeleton className="size-3.5 shrink-0" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="flex h-[18px] items-center"><Skeleton className="h-3.5 max-w-full" style={{ width: name }} /></span>
              <span className="flex h-4 items-center"><Skeleton className="h-3 max-w-full" style={{ width: sub }} /></span>
            </div>
          </div>
          <span className="hidden w-[150px] justify-end sm:flex"><Skeleton className="h-3.5" style={{ width: a }} /></span>
          <span className="hidden w-[150px] justify-end sm:flex"><Skeleton className="h-3.5" style={{ width: b }} /></span>
          <span className="flex w-[200px] items-center justify-end gap-3">
            <Skeleton className="hidden h-1 w-[120px] rounded-[2px] sm:block" />
            <Skeleton className="h-3.5 w-[39px]" />
          </span>
        </div>
      ))}
    </Card>
  )
}

function StripSkeleton({ title, subtitle, labels }: { title?: string; subtitle?: string; labels?: string[] }) {
  const steps = labels ?? Array.from({ length: 5 }, () => '')
  return (
    <Card className="gap-0 py-0">
      <div className="flex flex-wrap items-baseline gap-x-2.5 px-5 pt-4 pb-1">
        {title ? <h3 className="text-sm font-medium leading-[18px] text-foreground">{title}</h3> : <Skeleton className="h-3.5 w-28" />}
        {subtitle && <span className="text-[13px] leading-4 text-ui-secondary">{subtitle}</span>}
      </div>
      <div className="flex flex-wrap gap-y-4 px-5 pt-3.5 pb-[18px] md:flex-nowrap">
        {steps.map((label, i) => (
          <div key={i} className="contents">
            {i > 0 && (
              <div className="hidden w-[72px] shrink-0 flex-col items-center justify-center gap-1 md:flex">
                <Skeleton className="h-3 w-[26px]" />
                <StepArrow faint />
              </div>
            )}
            <div className="flex min-w-[45%] flex-1 basis-0 flex-col gap-1 md:min-w-0">
              {label ? <span className="text-xs leading-4 text-muted-foreground">{label}</span> : <Skeleton className="my-[3px] h-2.5 w-20" />}
              <span className="flex h-7 items-center"><Skeleton className="h-5 w-[62px]" /></span>
              <Skeleton className="h-3 w-[110px] max-w-full" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

/** Labels are known before the numbers: they render, values pulse. */
export function AttributionLoading({
  banner,
  spentLabel = 'Spent (ads)',
  earnedLabel = 'Earned (Stripe)',
}: {
  banner?: ReactNode
  spentLabel?: string
  earnedLabel?: string
}) {
  return (
    <div className="flex w-full flex-col gap-4" aria-busy="true" aria-label="Loading attribution">
      <div className="flex flex-col gap-1 py-1">
        <span className="flex h-7 items-center"><Skeleton className="h-7 w-[60%]" /></span>
        <span className="flex h-4 items-center"><Skeleton className="h-[13px] w-[38%]" /></span>
      </div>
      {banner}
      <div className="well flex flex-col gap-2">
        <Card className="gap-0 overflow-hidden py-0">
          <MoneyTiles>
            <TileSkeleton first label="Earned from ads" wide />
            <TileSkeleton label="Spent on ads" />
            <TileSkeleton label="Return per $1 spent" />
          </MoneyTiles>
          <ChartSkeleton
            legend={
              <>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-[2px] bg-chart-revenue" />
                  <span className="text-xs leading-4 text-muted-foreground">{earnedLabel}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="size-2 rounded-[2px] bg-muted-foreground/25" />
                  <span className="text-xs leading-4 text-muted-foreground">{spentLabel}</span>
                </span>
              </>
            }
          />
        </Card>
        <TableSkeleton title="Campaigns" columns={['Campaign', 'Spent', 'Earned', 'Return per $1']} />
        <StripSkeleton
          title="From ad to payment"
          labels={['Ads shown', 'Clicked', 'Landed on your site', 'Started a trial', 'Paid · Stripe']}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// First sync (Paper "Attribution · First sync · 1440"). Not wired: the API
// exposes no Stripe sync progress yet.
// ---------------------------------------------------------------------------

export function AttributionFirstSync({
  day,
  totalDays,
  readyNote,
}: {
  /** Days of payments pulled so far */
  day: number
  totalDays: number
  /** "Meta spend is ready" */
  readyNote?: string | null
}) {
  const pct = totalDays > 0 ? Math.min(1, day / totalDays) : 0
  return (
    <div className="flex w-full flex-col gap-4" aria-busy="true">
      <div className="flex flex-col gap-2.5 py-1">
        <h2 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
          Pulling {totalDays} days from Stripe, about a minute.
        </h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalDays}
            aria-valuenow={day}
            className="relative h-1 w-[320px] max-w-full rounded-[2px] bg-border"
          >
            <span className="absolute inset-y-0 left-0 rounded-[2px] bg-ui-ink" style={{ width: `${pct * 100}%` }} />
          </span>
          <span className="text-[13px] leading-4 text-ui-secondary">
            Day {day} of {totalDays}
            {readyNote ? ` · ${readyNote}` : ''} · you can leave this page, we’ll keep going
          </span>
        </div>
      </div>
      <div className="well flex flex-col gap-2">
        <Card className="gap-0 overflow-hidden py-0">
          <MoneyTiles>
            <TileSkeleton first />
            <TileSkeleton />
            <TileSkeleton />
          </MoneyTiles>
          <ChartSkeleton />
        </Card>
        <TableSkeleton title="Campaigns" />
        <StripSkeleton title="From ad to payment" />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Nothing connected (Paper "Attribution · Nothing connected · 1440")
// ---------------------------------------------------------------------------

const PREVIEW_BARS = [70, 98, 126, 84, 112, 70, 98]

export function AttributionNothingConnected({
  onConnectStripe,
  onConnectMeta,
  onConnectGoogle,
}: {
  onConnectStripe: () => void
  onConnectMeta: () => void
  onConnectGoogle: () => void
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <ReportHeader
        title="See which sources and ads turn into paying customers."
        meta="Connect Stripe and every payment is credited to the visit that brought it in. Ads add spend and return per $1."
      />

      <div className="flex flex-col rounded-xl bg-card shadow-[0_1px_2px_rgb(0_0_0/0.03)] ring-1 ring-border">
        <div className="flex flex-wrap items-center gap-3.5 py-[18px] pl-5 pr-[18px] sm:flex-nowrap">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-muted/60 ring-1 ring-border">
            <StripeMark />
          </span>
          {/* Wraps the button under the text on a phone instead of squeezing it */}
          <div className="flex min-w-0 flex-1 basis-[200px] flex-col gap-[3px] sm:basis-0">
            <span className="text-sm font-semibold leading-[18px] text-foreground">Start with Stripe</span>
            <span className="text-[13px] leading-4 text-muted-foreground">
              Revenue is the anchor. A read-only key pulls your last 30 days of payments in about a minute.
            </span>
          </div>
          <Button size="sm" className="h-8 px-3.5 text-[13px]" onClick={onConnectStripe}>
            Connect Stripe
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-b-xl border-t border-ui-hairline bg-muted/60 py-3 pl-5 pr-[18px] sm:flex-nowrap sm:pl-[70px]">
          <span className="min-w-0 flex-1 basis-full text-[13px] leading-4 text-muted-foreground sm:basis-0">
            Running ads? Connect them too and each campaign gets spend and return per $1.
          </span>
          <Button variant="outline" size="sm" className="h-7 gap-2 px-3 text-[13px]" onClick={onConnectMeta}>
            <MetaMark />
            Connect Meta Ads
          </Button>
          <Button variant="outline" size="sm" className="h-7 gap-2 px-3 text-[13px]" onClick={onConnectGoogle}>
            <GoogleAdsMark />
            Connect Google Ads
          </Button>
        </div>
      </div>

      {/* A preview of the page once connected: labels only, never invented numbers */}
      <div className="well flex flex-col gap-2" aria-hidden>
        <p className="px-2.5 pt-1 pb-0.5 text-xs leading-4 text-ui-meta">
          Preview · what this page shows once Stripe is connected
        </p>
        <div className="flex flex-col overflow-hidden rounded-xl bg-card ring-1 ring-foreground/5">
          <div className="flex border-b border-ui-hairline">
            {['Earned from ads', 'Spent on ads', 'Return per $1 spent'].map((label, i) => (
              <div
                key={label}
                className={cn(
                  'flex min-w-0 flex-1 basis-0 flex-col gap-3 px-[18px] py-4',
                  i === 0 ? 'shadow-[inset_0_-1.5px_0_rgb(16_17_18/0.15)]' : 'border-l border-ui-hairline',
                )}
              >
                <span className="truncate text-xs leading-4 text-ui-meta">{label}</span>
                <span className="h-5 w-28 max-w-full rounded-md bg-muted" />
              </div>
            ))}
          </div>
          <div className="flex gap-4 px-5 pt-4 text-xs leading-4 text-ui-meta">
            <span>Earned (Stripe)</span>
            <span>Spent (ads)</span>
          </div>
          <div className="flex px-5 pt-4 pb-5 sm:pl-16">
            <div className="flex min-w-0 flex-1 border-b border-foreground/[0.06]">
              {PREVIEW_BARS.map((h, i) => (
                <div key={i} className="flex h-[150px] min-w-0 flex-1 basis-0 items-end justify-center gap-1">
                  <span className="w-[26px] rounded-[3px] bg-muted" style={{ height: 70 }} />
                  <span className="w-[26px] rounded-[3px] bg-border" style={{ height: h }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col overflow-hidden rounded-xl bg-card ring-1 ring-foreground/5">
          <div className="border-b border-ui-hairline px-[18px] py-3.5 text-sm font-medium leading-[18px] text-ui-secondary">
            Where paying customers came from
          </div>
          <div className="flex items-center border-b border-ui-hairline bg-muted/60 px-[18px] py-2.5 text-xs leading-4 text-ui-meta">
            <span className="flex-1">Campaign or source</span>
            <span className="hidden w-[150px] justify-end sm:flex">Spent</span>
            <span className="hidden w-[150px] justify-end sm:flex">Earned</span>
            <span className="flex w-[110px] justify-end sm:w-[200px]">Return per $1</span>
          </div>
          {[[180, 220], [150, 140], [200, 180]].map(([a, b], i) => (
            <div key={i} className="flex items-center border-b border-ui-rowline px-[18px] py-3.5 last:border-b-0">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <span className="h-2.5 max-w-full rounded bg-border" style={{ width: a }} />
                <span className="h-2 max-w-full rounded bg-muted" style={{ width: b }} />
              </div>
              <span className="hidden w-[150px] justify-end sm:flex"><span className="h-2.5 w-[52px] rounded bg-border" /></span>
              <span className="hidden w-[150px] justify-end sm:flex"><span className="h-2.5 w-[52px] rounded bg-border" /></span>
              <span className="flex w-[110px] items-center justify-end gap-3 sm:w-[200px]">
                <span className="hidden h-1 w-[120px] rounded-[2px] bg-muted sm:block" />
                <span className="h-2.5 w-9 rounded bg-border" />
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col overflow-hidden rounded-xl bg-card ring-1 ring-foreground/5">
          <div className="px-5 pt-4 pb-1 text-sm font-medium leading-[18px] text-ui-secondary">From ad to payment</div>
          <div className="flex flex-wrap items-center gap-y-4 px-5 pt-4 pb-5 md:flex-nowrap">
            {['Ads shown', 'Clicked', 'Landed on your site', 'Started a trial', 'Paid · Stripe'].map((label, i) => (
              <div key={label} className="contents">
                {i > 0 && (
                  <span className="hidden w-[72px] shrink-0 justify-center md:flex">
                    <StepArrow faint />
                  </span>
                )}
                <div className="flex min-w-[45%] flex-1 basis-0 flex-col gap-2.5 md:min-w-0">
                  <span className="text-xs leading-4 text-ui-meta">{label}</span>
                  <span className="h-5 w-[72px] rounded-[5px] bg-muted" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// No payments in range (Paper "Attribution · No payments in range · 1440")
// ---------------------------------------------------------------------------

export function AttributionNoPayments({
  phrase,
  meta,
  hasAds,
  spend,
  trials,
  privacyMode = false,
  showShortRangeHint,
  wider,
  pickDates,
  actions,
}: {
  /** "the last 24 hours" */
  phrase: string
  meta?: string | null
  hasAds: boolean
  spend: number
  trials: number
  privacyMode?: boolean
  /** Payments land about a day after the click: say so on short ranges */
  showShortRangeHint: boolean
  /** A wider preset to jump to ("Show last 30 days") */
  wider?: { label: string; onSelect: () => void } | null
  /** The custom-range picker, rendered by the page */
  pickDates?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <ReportHeader title={`No payments in ${phrase} yet.`} meta={meta} actions={actions} />
      <div className="well flex flex-col gap-2">
        <Card className="gap-0 overflow-hidden py-0">
          <MoneyTiles>
            {hasAds ? (
              <>
                <MoneyTile first selected label="Earned from ads" value={<EmptyValue>None yet</EmptyValue>} />
                <MoneyTile label="Spent on ads" value={privatize(fmtMoney(spend), privacyMode)} />
                <MoneyTile label="Return per $1 spent" value={<EmptyValue>—</EmptyValue>} />
              </>
            ) : (
              <>
                <MoneyTile first selected label="Earned" value={<EmptyValue>None yet</EmptyValue>} />
                <MoneyTile label="Payments" value={<EmptyValue>—</EmptyValue>} />
                <MoneyTile label="Trials started" value={privatize(fmtCount(trials), privacyMode)} />
              </>
            )}
          </MoneyTiles>
          <div className="flex flex-col items-center gap-4 px-6 pt-[72px] pb-20">
            <div className="flex max-w-[520px] flex-col items-center gap-1.5 text-center">
              <h3 className="text-sm font-semibold leading-[18px] text-foreground">Nothing to credit in {phrase}</h3>
              <p className="text-[13px] leading-5 text-muted-foreground">
                {showShortRangeHint
                  ? 'Payments usually land about a day after the click, so short ranges often read empty.'
                  : `Stripe has no payments from ${phrase}.`}
              </p>
            </div>
            {(wider || pickDates) && (
              <div className="flex flex-wrap justify-center gap-2">
                {wider && (
                  <Button size="sm" className="h-8 px-3.5 text-[13px]" onClick={wider.onSelect}>
                    {wider.label}
                  </Button>
                )}
                {pickDates}
              </div>
            )}
            <p className="text-center text-xs leading-4 text-ui-meta">
              New Stripe account? This fills in as your first payments arrive.
            </p>
          </div>
        </Card>
      </div>
    </div>
  )
}
